import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { TechnicianApplications } from "../components/domain/TechnicianApplications.js";
import { FILTER_INPUT, FilterField, FilterPanel } from "../components/common/FilterBar.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;
/**
 * Bases aceitas pela aplicação (e pelo enum DB após migration 0041).
 */
type CommissionBase = "orcamento" | "faturado";

/**
 * F-tcc2 — a comissão padrão do técnico (defaultCommissionPercent/Base) e o
 * intermediário no nível do TÉCNICO foram DESCONTINUADOS: a comissão é
 * configurada exclusivamente por par técnico×cliente (seção "Comissões por
 * cliente"), cada par com % + base + intermediário próprios.
 */
interface TechnicianForm {
  name: string;
  email: string;
  phone: string;
  taxId: string;
  taxIdKind: string;
  countryCode: string;
  parcServiceRole: "" | "debosselage" | "montage";
  /** F-tecnico-senior — autorizado a ALTERAR a franquia no portal. */
  isSenior: boolean;
  /** F-tech-portal — id do usuário do portal ("" = sem conta vinculada). */
  portalUserId: string;
  notes: string;
  reason: string;
}

const emptyForm: TechnicianForm = {
  name: "",
  email: "",
  phone: "",
  taxId: "",
  taxIdKind: "",
  countryCode: "FR",
  parcServiceRole: "",
  isSenior: false,
  portalUserId: "",
  notes: "",
  reason: "",
};

export default function Technicians() {
  const { user } = useAuth();
  const tr = useT();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("active");
  const [offset, setOffset] = useState(0);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      q: q || undefined,
      active: activeFilter === "all" ? undefined : activeFilter === "active",
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, q, activeFilter, offset],
  );

  const listQuery = trpc.technicians.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const utils = trpc.useUtils();

  // F-worklog-no-pair — /tecnicos?editar=<id> abre direto o modal de edição
  // (deep-link do aviso "sem par de comissão" no worklog do técnico).
  // Abre direto na aba pedida pela URL — a pendência do Painel aponta para
  // /tecnicos?aba=candidaturas.
  const [aba, setAba] = useState<"lista" | "candidaturas">(() =>
    new URLSearchParams(window.location.search).get("aba") === "candidaturas"
      ? "candidaturas"
      : "lista",
  );
  // F-tech-signup — a fila do auto-cadastro vive ao lado da lista: o contador
  // no rótulo é o que faz o escritório lembrar de olhar.
  const appsQuery = trpc.technicianApplications.list.useQuery(
    { status: "pending", limit: 1 },
    { enabled: canEdit },
  );
  const pendentes = appsQuery.data?.pendingCount ?? 0;

  const [modal, setModal] = useState<
    { mode: "create" } | { mode: "edit"; technicianId: number } | null
  >(() => {
    const v = Number(new URLSearchParams(window.location.search).get("editar"));
    return Number.isInteger(v) && v > 0 ? { mode: "edit", technicianId: v } : null;
  });

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{tr("technicians.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const data = listQuery.data;
  const total = data?.total ?? 0;

  return (
    <Shell>
      {canEdit && (
        <nav className="flex flex-wrap gap-1 rounded-lg border border-neutral-800 bg-neutral-900 px-2 py-1">
          {(
            [
              ["lista", tr("technicians.tabActive")],
              ["candidaturas", tr("technicians.tabApplications")],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setAba(k)}
              className={`rounded-md px-3 py-1.5 text-sm ${
                aba === k
                  ? "border border-lime/40 bg-lime/20 text-lime"
                  : "text-neutral-400 hover:text-neutral-200"
              }`}
            >
              {label}
              {k === "candidaturas" && pendentes > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-500/20 px-1.5 text-[10px] text-amber-300">
                  {pendentes}
                </span>
              )}
            </button>
          ))}
        </nav>
      )}
      {aba === "candidaturas" ? (
        <TechnicianApplications companyId={companyId} />
      ) : (
        <>
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <FilterField label={tr("technicians.companyLabel")}>
              <select
                value={companyId ?? ""}
                onChange={(e) => {
                  setCompanyId(Number(e.target.value));
                  setOffset(0);
                }}
                className={inputClass + " w-64"}
              >
                {accessibleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FilterField>
            {canEdit && (
              <button
                onClick={() => setModal({ mode: "create" })}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
              >
                {tr("technicians.newBtn")}
              </button>
            )}
          </div>

          <FilterPanel testId="technicians-filter-panel">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <FilterField label={tr("technicians.searchLabel")} className="grow max-w-xs">
                <input
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setOffset(0);
                  }}
                  placeholder={tr("technicians.searchPlaceholder")}
                  className={FILTER_INPUT + " w-full"}
                />
              </FilterField>
              <FilterField label={tr("technicians.status")}>
                <select
                  value={activeFilter}
                  onChange={(e) => {
                    setActiveFilter(e.target.value as typeof activeFilter);
                    setOffset(0);
                  }}
                  className={FILTER_INPUT + " w-full sm:w-40"}
                >
                  <option value="active">{tr("technicians.filterActive")}</option>
                  <option value="inactive">{tr("technicians.filterInactive")}</option>
                  <option value="all">{tr("technicians.filterAll")}</option>
                </select>
              </FilterField>
            </div>
          </FilterPanel>

          <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
            {listQuery.isLoading && (
              <p className="p-4 text-sm text-neutral-500">{tr("technicians.loading")}</p>
            )}
            {listQuery.error && (
              <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>
            )}
            {data && data.rows.length === 0 && (
              <p className="p-4 text-sm text-neutral-500">{tr("technicians.emptyState")}</p>
            )}
            {data && data.rows.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="text-left px-3 py-2">{tr("technicians.name")}</th>
                    <th className="text-left px-3 py-2">{tr("technicians.email")}</th>
                    <th className="text-left px-3 py-2">{tr("technicians.colTaxId")}</th>
                    <th className="text-left px-3 py-2 w-36">{tr("technicians.colCommission")}</th>
                    <th className="text-left px-3 py-2 w-20">{tr("technicians.status")}</th>
                    <th className="px-3 py-2 w-20 text-right">{tr("technicians.colActions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {data.rows.map((t) => (
                    <tr key={t.id} className={t.active ? "" : "opacity-60"}>
                      {/* CEO 2026-09-13: abrir clicando direto no nome. */}
                      <td className="px-3 py-2">
                        <Link
                          href={`/tecnicos/${t.id}`}
                          className="font-medium text-lime hover:underline"
                        >
                          {t.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-400">{t.email ?? "—"}</td>
                      <td className="px-3 py-2 text-xs text-neutral-400">
                        {t.taxId ?? <span className="text-neutral-700">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-400">
                        {tr("technicians.perCustomer")}
                      </td>
                      <td className="px-3 py-2">
                        <ActiveBadge active={t.active} />
                        {t.portalBlocked && (
                          <span
                            className="ml-1 inline-block rounded bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300 whitespace-nowrap"
                            data-testid="tech-portal-blocked"
                          >
                            🔒 {tr("technicians.badgeBlocked")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          {canEdit && (
                            <button
                              onClick={() => setModal({ mode: "edit", technicianId: t.id })}
                              className="text-xs text-neutral-400 hover:underline"
                            >
                              {tr("technicians.edit")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {total > 0 && (
            <Paginacao
              total={total}
              pageSize={PAGE_SIZE}
              offset={offset}
              onOffset={setOffset}
              noun={
                total === 1
                  ? tr("technicians.technicianSingular")
                  : tr("technicians.technicianPlural")
              }
              testId="technicians-pagination"
            />
          )}
        </>
      )}

      {canEdit && modal && companyId !== null && (
        <TechnicianModal
          companyId={companyId}
          mode={modal.mode}
          technicianId={modal.mode === "edit" ? modal.technicianId : undefined}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            utils.technicians.invalidate();
          }}
        />
      )}
    </Shell>
  );
}

function TechnicianModal({
  companyId,
  mode,
  technicianId,
  onClose,
  onSaved,
}: {
  companyId: number;
  mode: "create" | "edit";
  technicianId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const t = useT();
  const modals = useModals();
  // F-tcc2 — empresas elegíveis a intermediário POR PAR (excluindo a empresa do técnico)
  const companiesQuery = trpc.companies.list.useQuery();
  const intermediaryCandidates = (companiesQuery.data ?? []).filter(
    (c) => c.id !== companyId && (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  // F-billing-company-label — a empresa ATIVA é quem absorve o resíduo da
  // cascata (ES ou BS); a prévia mostra o nome dela, não "Euro Storm" fixo.
  const billingCompanyName =
    (companiesQuery.data ?? []).find((c) => c.id === companyId)?.name ??
    t("technicians.theCompany");
  // Clientes ativos para o seletor "+ Vincular cliente" da seção de pares.
  const customersQuery = trpc.customers.list.useQuery(
    { companyId, active: true, limit: 200, offset: 0 },
    { staleTime: 60_000 },
  );
  const detailQuery = trpc.technicians.getById.useQuery(
    { companyId, technicianId: technicianId ?? 0 },
    {
      enabled: mode === "edit" && !!technicianId,
      retry: false,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
  const [form, setForm] = useState<TechnicianForm>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit") return;
    const d = detailQuery.data;
    if (!d) return;
    setForm({
      name: d.name,
      email: d.email ?? "",
      phone: d.phone ?? "",
      taxId: d.taxId ?? "",
      taxIdKind: d.taxIdKind ?? "",
      countryCode: d.countryCode,
      parcServiceRole: d.parcServiceRole ?? "",
      isSenior: d.isSenior ?? false,
      portalUserId: d.userId != null ? String(d.userId) : "",
      notes: d.notes ?? "",
      reason: "",
    });
  }, [mode, detailQuery.data?.id, detailQuery.dataUpdatedAt]);

  const isAdmin =
    user?.isSuperAdmin ||
    user?.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin") ||
    false;

  // F-tech-portal — usuários da empresa para vincular a conta do portal
  // (endpoint é admin-only; o select só aparece para admin).
  const companyUsersQuery = trpc.users.listByCompany.useQuery(
    { companyId },
    { enabled: isAdmin, staleTime: 60_000 },
  );
  const portalCandidates = (companyUsersQuery.data ?? []).filter(
    (u) => u.active && u.membershipActive,
  );

  const taxChanged =
    mode === "edit" && detailQuery.data
      ? form.taxId !== (detailQuery.data.taxId ?? "") ||
        form.taxIdKind !== (detailQuery.data.taxIdKind ?? "")
      : false;

  const createMutation = trpc.technicians.create.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const updateMutation = trpc.technicians.update.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const deactivateMutation = trpc.technicians.deactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });
  const reactivateMutation = trpc.technicians.reactivate.useMutation({
    onSuccess: onSaved,
    onError: (err) => setError(err.message),
  });

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deactivateMutation.isPending ||
    reactivateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const payload = {
      companyId,
      name: form.name.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      taxId: form.taxId.trim() || undefined,
      taxIdKind: form.taxIdKind.trim() || undefined,
      countryCode: form.countryCode.trim().toUpperCase(),
      parcServiceRole: form.parcServiceRole || null,
      isSenior: form.isSenior,
      // F-tech-portal — vínculo com a conta do portal (só admin vê o select).
      userId: form.portalUserId ? Number(form.portalUserId) : null,
      notes: form.notes.trim() || undefined,
    };
    if (mode === "create") {
      createMutation.mutate(payload);
    } else if (technicianId) {
      const { companyId: _drop, ...patch } = payload;
      updateMutation.mutate({
        companyId,
        technicianId,
        patch,
        reason: form.reason.trim() || undefined,
      });
    }
  }

  return (
    <ModalShell
      title={
        mode === "create" ? t("technicians.modalTitleCreate") : t("technicians.modalTitleEdit")
      }
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("technicians.name")} required>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={200}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("technicians.fieldCountry")} required>
            <input
              value={form.countryCode}
              onChange={(e) =>
                setForm({ ...form, countryCode: e.target.value.toUpperCase().slice(0, 2) })
              }
              required
              maxLength={2}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("technicians.email")}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("technicians.fieldPhone")}>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled={pending}
              className={inputClass}
            />
          </Field>
          <Field label={t("technicians.fieldTaxId")}>
            <input
              value={form.taxId}
              onChange={(e) => setForm({ ...form, taxId: e.target.value })}
              maxLength={50}
              disabled={pending || (mode === "edit" && !isAdmin)}
              className={inputClass}
            />
            {mode === "edit" && !isAdmin && (
              <p className="text-xs text-neutral-600 mt-1">{t("technicians.taxIdAdminOnly")}</p>
            )}
          </Field>
          <Field label={t("technicians.fieldTaxIdKind")}>
            <select
              value={form.taxIdKind}
              onChange={(e) => setForm({ ...form, taxIdKind: e.target.value })}
              disabled={pending || (mode === "edit" && !isAdmin)}
              className={inputClass}
            >
              <option value="">{t("technicians.dash")}</option>
              <option value="siret">SIRET (FR)</option>
              <option value="rib">RIB (FR)</option>
              <option value="vat_gb">VAT (UK)</option>
              <option value="vat_de">USt-IdNr. (DE)</option>
              <option value="vat_it">Partita IVA (IT)</option>
              <option value="vat_es">NIF/CIF (ES)</option>
              <option value="vat_eu">{t("technicians.taxKindVatEu")}</option>
              <option value="cpf">CPF (BR)</option>
              <option value="ee_reg">Äriregister (EE)</option>
              <option value="other">{t("technicians.taxKindOther")}</option>
            </select>
          </Field>
        </div>

        {/* ─── F-parc — tipo de serviço no Parc Grêle ─────────────────────── */}
        <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3">
          <Field label={t("technicians.fieldParcRole")}>
            <select
              value={form.parcServiceRole}
              onChange={(e) =>
                setForm({
                  ...form,
                  parcServiceRole: e.target.value as TechnicianForm["parcServiceRole"],
                })
              }
              disabled={pending}
              className={inputClass}
            >
              <option value="">{t("technicians.parcRoleNone")}</option>
              <option value="debosselage">Débosselage</option>
              <option value="montage">{t("technicians.parcRoleMontage")}</option>
            </select>
          </Field>
          <p className="mt-1 text-[11px] text-neutral-500">{t("technicians.parcRoleHint")}</p>
        </div>

        {/* ─── F-tecnico-senior — autorização para mexer na franquia ─────── */}
        <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isSenior}
              disabled={pending}
              onChange={(e) => setForm({ ...form, isSenior: e.target.checked })}
            />
            {t("technicians.fieldSenior")}
          </label>
          <p className="mt-1 text-[11px] text-neutral-500">{t("technicians.seniorHint")}</p>
        </div>

        {/* ─── F-tech-portal — conta de login do portal do técnico ────────── */}
        {isAdmin && (
          <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3">
            <Field label={t("technicians.fieldPortalUser")}>
              <select
                value={form.portalUserId}
                onChange={(e) => setForm({ ...form, portalUserId: e.target.value })}
                disabled={pending}
                className={inputClass}
              >
                <option value="">{t("technicians.portalUserNone")}</option>
                {portalCandidates.map((u) => (
                  <option key={u.userId} value={String(u.userId)}>
                    {u.name} · {u.email}
                    {u.role === "technician" ? "" : ` (${u.role})`}
                  </option>
                ))}
              </select>
            </Field>
            <p className="mt-1 text-[11px] text-neutral-500">{t("technicians.portalUserHint")}</p>
          </div>
        )}

        {/* ─── F-tcc2 — Comissões por cliente (par técnico×cliente) ───────── */}
        {mode === "edit" && technicianId != null ? (
          <ClientCommissionsSection
            companyId={companyId}
            technicianId={technicianId}
            customers={customersQuery.data?.rows ?? []}
            intermediaryCandidates={intermediaryCandidates}
            billingCompanyName={billingCompanyName}
            disabled={pending}
          />
        ) : (
          <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3 text-xs text-neutral-500">
            {t("technicians.commissionsSaveFirst")}
          </div>
        )}

        {/* ─── F-chantier-commission v2 — Comissões por chantier ──────────── */}
        {mode === "edit" && technicianId != null && (
          <ChantierCommissionsSection
            companyId={companyId}
            technicianId={technicianId}
            intermediaryCandidates={intermediaryCandidates}
            disabled={pending}
          />
        )}

        <Field label={t("technicians.fieldNotes")}>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            maxLength={2000}
            rows={2}
            disabled={pending}
            className={`${inputClass} resize-y`}
          />
        </Field>

        {mode === "edit" && taxChanged && (
          <Field label={t("technicians.fieldTaxReason")} required>
            <input
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              required
              maxLength={500}
              disabled={pending}
              placeholder={t("technicians.taxReasonPlaceholder")}
              className={inputClass}
            />
          </Field>
        )}

        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
          <div className="flex gap-2">
            {mode === "edit" && technicianId && isAdmin && detailQuery.data && (
              <>
                {detailQuery.data.active ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const reason = await modals.promptReason({
                        title: `${t("technicians.deactivateTitle")} "${detailQuery.data?.name ?? ""}"`,
                        message: t("technicians.deactivateMessage"),
                        placeholder: t("technicians.deactivatePlaceholder"),
                        confirmLabel: t("technicians.deactivate"),
                        tone: "danger",
                        required: true,
                      });
                      if (!reason) return;
                      deactivateMutation.mutate({ companyId, technicianId, reason });
                    }}
                    disabled={pending}
                    className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-1.5 text-xs"
                  >
                    {t("technicians.deactivate")}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => reactivateMutation.mutate({ companyId, technicianId })}
                    disabled={pending}
                    className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs"
                  >
                    {t("technicians.reactivate")}
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {t("technicians.cancel")}
            </button>
            <button
              type="submit"
              disabled={pending}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
            >
              {pending
                ? t("technicians.saving")
                : mode === "create"
                  ? t("technicians.create")
                  : t("technicians.save")}
            </button>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── shared bits ────────────────────────────────────────────────────────────────

const inputClass =
  "w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-neutral-400 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

/** Cliente como visto pelos seletores da seção de pares (lista de clientes ativos). */
interface PreviewCustomer {
  id: number;
  displayName: string;
  defaultRetentionPct: string | number | null;
}

/**
 * F-tcc / F-tcc2 — Comissões por par técnico×cliente. Cada cliente vinculado
 * vira um item colapsável (accordion) com % + base + intermediário PRÓPRIOS.
 * Precedência no cálculo: override da line (UI) > par. SEM par configurado o
 * cálculo é bloqueado (a comissão padrão do técnico foi descontinuada).
 */
/**
 * F-chantier-commission v2 (CEO 2026-09-17) — comissão POR TÉCNICO × CHANTIER:
 * "quando adiciono um chantier a um técnico defino a comissão do técnico para
 * aquele chantier" — vale para TODOS os clientes do chantier sem par
 * técnico×cliente específico (o par, quando existir, vence). A remise segue
 * do CLIENTE (cadastro) — o chantier não define remise.
 */
function ChantierCommissionsSection({
  companyId,
  technicianId,
  intermediaryCandidates,
  disabled,
}: {
  companyId: number;
  technicianId: number;
  intermediaryCandidates: { id: number; name: string }[];
  disabled: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const chantiersQ = trpc.chantiers.list.useQuery({ companyId, activeOnly: false });
  const listQ = trpc.technicians.listChantierCommissions.useQuery({ companyId, technicianId });
  const invalidate = () => {
    void utils.technicians.listChantierCommissions.invalidate({ companyId, technicianId });
    void utils.technicians.customersWithoutPair.invalidate({ companyId, technicianId });
  };
  const upsertMut = trpc.technicians.upsertChantierCommission.useMutation({
    onSuccess: invalidate,
  });
  const deleteMut = trpc.technicians.deleteChantierCommission.useMutation({
    onSuccess: invalidate,
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<{
    pct: string;
    base: "orcamento" | "faturado";
    intermId: string;
    intermPct: string;
  }>({ pct: "", base: "faturado", intermId: "", intermPct: "" });

  const linked = listQ.data ?? [];
  const linkedIds = new Set(linked.map((l) => l.chantierId));
  const available = (chantiersQ.data?.rows ?? []).filter((c) => !linkedIds.has(c.id));
  const busy = disabled || upsertMut.isPending || deleteMut.isPending;

  function openChantier(chantierId: number, row?: (typeof linked)[number]) {
    setExpandedId(chantierId);
    setDraft({
      pct: row?.commissionPct != null ? String(Number(row.commissionPct)) : "",
      base: row?.commissionBase ?? "faturado",
      intermId: row?.intermediaryCompanyId != null ? String(row.intermediaryCompanyId) : "",
      intermPct: row?.intermediaryPct != null ? String(Number(row.intermediaryPct)) : "",
    });
  }

  function save(chantierId: number) {
    const pct = Number(draft.pct);
    if (!Number.isFinite(pct)) return;
    upsertMut.mutate({
      companyId,
      technicianId,
      chantierId,
      commissionPct: pct,
      commissionBase: draft.base,
      intermediaryCompanyId: draft.intermId === "" ? null : Number(draft.intermId),
      intermediaryPct:
        draft.intermId === "" || draft.intermPct === "" ? null : Number(draft.intermPct),
    });
  }

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium text-neutral-300">
          🏗 {t("technicians.chantierCommTitle")}
        </span>
        <span className="text-[11px] text-neutral-500">{t("technicians.chantierCommHint")}</span>
      </div>

      <select
        value=""
        onChange={(e) => {
          const id = Number(e.target.value);
          if (id) openChantier(id);
        }}
        disabled={busy || available.length === 0}
        className="w-full rounded border border-lime/40 bg-neutral-900 px-2 py-1.5 text-sm text-lime focus:border-lime outline-none disabled:opacity-50 disabled:text-neutral-500 cursor-pointer"
      >
        <option value="">
          {available.length === 0
            ? t("technicians.chantierAllLinked")
            : t("technicians.chantierLinkOption")}
        </option>
        {available.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {c.name}
          </option>
        ))}
      </select>

      {upsertMut.error && <p className="text-xs text-red-400">{upsertMut.error.message}</p>}
      {deleteMut.error && <p className="text-xs text-red-400">{deleteMut.error.message}</p>}

      {[...linked, ...(expandedId != null && !linkedIds.has(expandedId) ? [null] : [])].map((l) => {
        const chantierId = l?.chantierId ?? expandedId!;
        const name =
          l?.chantierName ??
          (chantiersQ.data?.rows ?? []).find((c) => c.id === chantierId)?.name ??
          `#${chantierId}`;
        const isOpen = expandedId === chantierId;
        const pctNum = l != null ? Number(l.commissionPct) : null;
        return (
          <div key={chantierId} className="rounded border border-neutral-800">
            <button
              type="button"
              onClick={() =>
                isOpen ? setExpandedId(null) : openChantier(chantierId, l ?? undefined)
              }
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
            >
              <span className="font-medium text-neutral-200">{name}</span>
              <span className="flex items-center gap-2 text-xs">
                {l != null ? (
                  <span className={pctNum != null && pctNum > 0 ? "text-lime" : "text-amber-400"}>
                    {Number(l.commissionPct).toFixed(2)}% ·{" "}
                    {l.commissionBase === "orcamento"
                      ? t("technicians.chantierBaseOrcamento")
                      : t("technicians.chantierBaseFaturado")}
                    {l.intermediaryPct != null &&
                      ` · interm. ${Number(l.intermediaryPct).toFixed(2)}%`}
                  </span>
                ) : (
                  <span className="text-amber-400">{t("technicians.chantierNewTag")}</span>
                )}
                <span className="text-neutral-500">{isOpen ? "▲" : "▼"}</span>
              </span>
            </button>
            {isOpen && (
              <div className="space-y-2 border-t border-neutral-800 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs uppercase tracking-wider text-neutral-500">
                    {t("technicians.chantierPctLabel")}
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={draft.pct}
                      onChange={(e) => setDraft({ ...draft, pct: e.target.value })}
                      className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-lime"
                    />
                  </label>
                  <label className="block text-xs uppercase tracking-wider text-neutral-500">
                    {t("technicians.chantierBaseLabel")}
                    <select
                      value={draft.base}
                      onChange={(e) =>
                        setDraft({ ...draft, base: e.target.value as "orcamento" | "faturado" })
                      }
                      className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-lime"
                    >
                      <option value="faturado">{t("technicians.chantierBaseFaturado")}</option>
                      <option value="orcamento">{t("technicians.chantierBaseOrcamento")}</option>
                    </select>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs uppercase tracking-wider text-neutral-500">
                    {t("technicians.chantierIntermLabel")}
                    <select
                      value={draft.intermId}
                      onChange={(e) => setDraft({ ...draft, intermId: e.target.value })}
                      className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-lime"
                    >
                      <option value="">{t("technicians.chantierIntermNone")}</option>
                      {intermediaryCandidates.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs uppercase tracking-wider text-neutral-500">
                    {t("technicians.chantierIntermPctLabel")}
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={draft.intermPct}
                      onChange={(e) => setDraft({ ...draft, intermPct: e.target.value })}
                      disabled={draft.intermId === ""}
                      className="mt-1 w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-lime disabled:opacity-50"
                    />
                  </label>
                </div>
                <p className="text-[11px] text-neutral-500">
                  {t("technicians.chantierRemiseNote")}
                </p>
                <div className="flex items-center justify-between">
                  {l != null ? (
                    <button
                      type="button"
                      onClick={() => deleteMut.mutate({ companyId, technicianId, chantierId })}
                      disabled={busy}
                      className="rounded-md border border-red-500/40 px-3 py-1.5 text-xs text-red-300 hover:border-red-500"
                    >
                      {t("technicians.chantierUnlink")}
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={() => save(chantierId)}
                    disabled={busy || draft.pct === ""}
                    className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime hover:opacity-90 disabled:opacity-40"
                  >
                    {t("technicians.chantierSave")}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ClientCommissionsSection({
  companyId,
  technicianId,
  customers,
  intermediaryCandidates,
  billingCompanyName,
  disabled,
}: {
  companyId: number;
  technicianId: number;
  customers: PreviewCustomer[];
  intermediaryCandidates: { id: number; name: string }[];
  billingCompanyName: string;
  disabled: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const listQ = trpc.technicians.listClientCommissions.useQuery({ companyId, technicianId });
  // F-tarifa-atelier (CEO 2026-10-10) — par por ATELIÊ/projeto do cliente
  // (REPARE GRELE: Stellantis 1 / 2 / Peugeot / KDMO / TLS).
  const ateliersAllQ = trpc.customers.listAteliersAll.useQuery({ companyId });
  const ateliersAll = ateliersAllQ.data?.rows ?? [];
  const chaveDoPar = (cid: number, aid: number | null | undefined) => `${cid}:${aid ?? ""}`;
  // F-external-intermediary Fase 2 — intermediários EXTERNOS elegíveis (ativos).
  const externalsQ = trpc.externalIntermediaries.list.useQuery({
    companyId,
    active: true,
    limit: 200,
    offset: 0,
  });
  const externalCandidates = (externalsQ.data?.rows ?? []).map((r) => ({ id: r.id, name: r.name }));
  // F-pair-alert — clientes com dossiers deste técnico mas SEM par configurado.
  const withoutPairQ = trpc.technicians.customersWithoutPair.useQuery({ companyId, technicianId });
  const withoutPair = withoutPairQ.data ?? [];
  const invalidate = () => {
    void utils.technicians.listClientCommissions.invalidate({ companyId, technicianId });
    // F-pair-alert — o aviso laranja lê customersWithoutPair; sem invalidar
    // aqui ele continuava aceso depois de vincular/ajustar o par.
    void utils.technicians.customersWithoutPair.invalidate({ companyId, technicianId });
  };
  const upsertMut = trpc.technicians.upsertClientCommission.useMutation({
    onSuccess: invalidate,
  });
  const deleteMut = trpc.technicians.deleteClientCommission.useMutation({
    onSuccess: invalidate,
  });

  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const linked = listQ.data ?? [];
  const linkedKeys = new Set(linked.map((l) => chaveDoPar(l.customerId, l.atelierId)));
  // Opções: o cliente (par geral) e cada ateliê do cliente (par do projeto).
  const available: Array<{
    key: string;
    customerId: number;
    atelierId: number | null;
    label: string;
  }> = [];
  for (const c of customers) {
    if (!linkedKeys.has(chaveDoPar(c.id, null))) {
      available.push({
        key: chaveDoPar(c.id, null),
        customerId: c.id,
        atelierId: null,
        label: c.displayName,
      });
    }
    for (const a of ateliersAll.filter((x) => x.customerId === c.id)) {
      if (!linkedKeys.has(chaveDoPar(c.id, a.id))) {
        available.push({
          key: chaveDoPar(c.id, a.id),
          customerId: c.id,
          atelierId: a.id,
          label: `${c.displayName} · 🏭 ${a.name}`,
        });
      }
    }
  }
  const busy = disabled || upsertMut.isPending || deleteMut.isPending;

  /**
   * F-tcc UX — escolher no dropdown JÁ VINCULA (sem botão separado, que ficava
   * cinza até a seleção e parecia "bloqueado"). Cria com defaults (0% /
   * faturado) e abre o item em accordion pra editar.
   */
  function linkCustomer(key: string) {
    const opt = available.find((o) => o.key === key);
    if (!opt) return;
    upsertMut.mutate(
      {
        companyId,
        technicianId,
        customerId: opt.customerId,
        atelierId: opt.atelierId,
        commissionPct: 0,
        commissionBase: "faturado",
      },
      { onSuccess: () => setExpandedKey(key) },
    );
  }

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium text-neutral-300">
          {t("technicians.commissionsSectionTitle")}
        </span>
        <span className="text-[11px] text-neutral-500">
          {t("technicians.commissionsSectionHint")}
        </span>
      </div>

      {withoutPair.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          <div className="font-medium text-amber-300">
            ⚠️ {withoutPair.length} {t("technicians.withoutPairCount")}
          </div>
          <div className="mt-0.5 text-amber-200/80">{t("technicians.withoutPairHint")}</div>
          <ul className="mt-1 space-y-0.5">
            {withoutPair.map((c) => (
              <li key={c.customerId}>
                • <b>{c.customerName}</b> — {c.dossierCount} {t("technicians.dossierCountSuffix")}{" "}
                <span className="text-amber-300/90">
                  ({c.zeroPair ? t("technicians.pairZeroTag") : t("technicians.pairMissingTag")})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Vincular cliente — selecionar já vincula */}
      <select
        value=""
        onChange={(e) => linkCustomer(e.target.value)}
        disabled={busy || available.length === 0}
        className="w-full rounded border border-lime/40 bg-neutral-900 px-2 py-1.5 text-sm text-lime focus:border-lime outline-none disabled:opacity-50 disabled:text-neutral-500 cursor-pointer"
      >
        <option value="">
          {available.length === 0
            ? t("technicians.allLinked")
            : t("technicians.linkCustomerOption")}
        </option>
        {available.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>

      {upsertMut.error && <p className="text-xs text-red-400">{upsertMut.error.message}</p>}
      {deleteMut.error && <p className="text-xs text-red-400">{deleteMut.error.message}</p>}
      {listQ.isLoading && (
        <p className="text-xs text-neutral-500">{t("technicians.loadingLinks")}</p>
      )}
      {!listQ.isLoading && linked.length === 0 && (
        <p className="text-xs text-amber-300">⚠ {t("technicians.noLinkedWarning")}</p>
      )}

      {/* Accordion */}
      {linked.map((l) => (
        <ClientCommissionItem
          key={chaveDoPar(l.customerId, l.atelierId)}
          row={l}
          expanded={expandedKey === chaveDoPar(l.customerId, l.atelierId)}
          onToggle={() =>
            setExpandedKey(
              expandedKey === chaveDoPar(l.customerId, l.atelierId)
                ? null
                : chaveDoPar(l.customerId, l.atelierId),
            )
          }
          onSave={(
            pct,
            base,
            intermediaryCompanyId,
            intermediaryExternalId,
            intermediaryPct,
            clientPct,
          ) =>
            upsertMut.mutate({
              companyId,
              technicianId,
              customerId: l.customerId,
              atelierId: l.atelierId,
              commissionPct: pct,
              commissionBase: base,
              intermediaryCompanyId,
              intermediaryExternalId,
              intermediaryPct,
              clientPct,
            })
          }
          onRemove={() =>
            deleteMut.mutate({
              companyId,
              technicianId,
              customerId: l.customerId,
              atelierId: l.atelierId,
            })
          }
          busy={busy}
          intermediaryCandidates={intermediaryCandidates}
          externalCandidates={externalCandidates}
          billingCompanyName={billingCompanyName}
        />
      ))}
    </div>
  );
}

function ClientCommissionItem({
  row,
  expanded,
  onToggle,
  onSave,
  onRemove,
  busy,
  intermediaryCandidates,
  externalCandidates,
  billingCompanyName,
}: {
  row: {
    customerId: number;
    customerName: string;
    /** F-tarifa-atelier — par do ateliê/projeto (null = par do cliente). */
    atelierId?: number | null;
    atelierName?: string | null;
    atelierRetentionPct?: string | null;
    commissionPct: string;
    commissionBase: CommissionBase;
    intermediaryCompanyId: number | null;
    intermediaryExternalId: number | null;
    intermediaryPct: string | null;
    clientPct: string | null;
    customerRetentionPct: string | null;
  };
  expanded: boolean;
  onToggle: () => void;
  onSave: (
    pct: number,
    base: CommissionBase,
    intermediaryCompanyId?: number,
    intermediaryExternalId?: number,
    intermediaryPct?: number,
    clientPct?: number | null,
  ) => void;
  onRemove: () => void;
  busy: boolean;
  intermediaryCandidates: { id: number; name: string }[];
  externalCandidates: { id: number; name: string }[];
  billingCompanyName: string;
}) {
  const t = useT();
  const [pct, setPct] = useState(String(Number(row.commissionPct)));
  const [base, setBase] = useState<CommissionBase>(row.commissionBase);
  // F-tcc2 / F-external-intermediary — intermediário por par. Ref codificada:
  // "c:<id>" (empresa do grupo) | "x:<id>" (externo) | "" (nenhum).
  const rowRef =
    row.intermediaryCompanyId != null
      ? `c:${row.intermediaryCompanyId}`
      : row.intermediaryExternalId != null
        ? `x:${row.intermediaryExternalId}`
        : "";
  const [hasIntermediary, setHasIntermediary] = useState(rowRef !== "");
  const [intRef, setIntRef] = useState(rowRef);
  const [intPct, setIntPct] = useState(
    row.intermediaryPct != null ? String(Number(row.intermediaryPct)) : "",
  );
  // F-pair-client-pct — remise do cliente NO PAR (vazio = usa o cadastro do cliente).
  const [cliPct, setCliPct] = useState(row.clientPct != null ? String(Number(row.clientPct)) : "");
  // Re-sincroniza quando o servidor confirma (ex.: outro item editado / refetch).
  useEffect(() => {
    setPct(String(Number(row.commissionPct)));
    setBase(row.commissionBase);
    setHasIntermediary(rowRef !== "");
    setIntRef(rowRef);
    setIntPct(row.intermediaryPct != null ? String(Number(row.intermediaryPct)) : "");
    setCliPct(row.clientPct != null ? String(Number(row.clientPct)) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    row.commissionPct,
    row.commissionBase,
    row.intermediaryCompanyId,
    row.intermediaryExternalId,
    row.intermediaryPct,
    row.clientPct,
  ]);

  const pctNum = Number(pct);
  const pctValid = pct !== "" && Number.isFinite(pctNum) && pctNum >= 0 && pctNum <= 100;
  const intPctNum = Number(intPct);
  const intValid =
    !hasIntermediary ||
    (intRef !== "" &&
      intPct !== "" &&
      Number.isFinite(intPctNum) &&
      intPctNum >= 0 &&
      intPctNum <= 100);
  const effectiveIntPct = hasIntermediary && Number.isFinite(intPctNum) ? intPctNum : 0;
  const cliPctNum = Number(cliPct);
  const cliValid =
    cliPct === "" || (Number.isFinite(cliPctNum) && cliPctNum >= 0 && cliPctNum <= 100);
  // Em base orçamento a soma inclui o cliente (do par, ou do cadastro se vazio).
  const effectiveCliPct =
    base === "orcamento"
      ? cliPct !== "" && Number.isFinite(cliPctNum)
        ? cliPctNum
        : row.atelierRetentionPct != null
          ? Number(row.atelierRetentionPct)
          : row.customerRetentionPct != null
            ? Number(row.customerRetentionPct)
            : 0
      : 0;
  const sumOk = effectiveCliPct + pctNum + effectiveIntPct <= 100;
  const valid = pctValid && intValid && cliValid && sumOk;
  const dirty =
    pctNum !== Number(row.commissionPct) ||
    base !== row.commissionBase ||
    hasIntermediary !== (rowRef !== "") ||
    (hasIntermediary && (intRef !== rowRef || intPctNum !== Number(row.intermediaryPct ?? 0))) ||
    cliPct !== (row.clientPct != null ? String(Number(row.clientPct)) : "");

  return (
    <div className="rounded border border-neutral-800 bg-neutral-950/40">
      {/* Cabeçalho colapsável */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="text-sm text-neutral-200 truncate">
          {row.customerName}
          {row.atelierName && <span className="text-neutral-400"> · 🏭 {row.atelierName}</span>}
        </span>
        <span className="flex items-center gap-3 text-xs text-neutral-400 whitespace-nowrap">
          <span className="tabular-nums">{Number(row.commissionPct).toFixed(2)}%</span>
          {row.clientPct != null && (
            <span className="tabular-nums text-[10px] text-neutral-500">
              {t("technicians.clientShort")} {Number(row.clientPct).toFixed(2)}%
            </span>
          )}
          {row.intermediaryPct != null && (
            <span className="tabular-nums text-[10px] text-neutral-500">
              {t("technicians.intermShort")} {Number(row.intermediaryPct).toFixed(2)}%
            </span>
          )}
          <span className="uppercase text-[10px] tracking-wider">
            {row.commissionBase === "orcamento"
              ? t("technicians.baseOrcamento")
              : t("technicians.baseFaturado")}
          </span>
          <span className="text-neutral-600">{expanded ? "▲" : "▼"}</span>
        </span>
      </button>
      {/* Corpo expandido */}
      {expanded && (
        <div className="border-t border-neutral-800 px-3 py-3 grid grid-cols-2 gap-3">
          <Field label={t("technicians.fieldCommissionPct")} required>
            <input
              type="number"
              step="0.01"
              min={0}
              max={100}
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              disabled={busy}
              className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none"
            />
          </Field>
          <Field label={t("technicians.fieldBase")} required>
            <select
              value={base}
              onChange={(e) => setBase(e.target.value as CommissionBase)}
              disabled={busy}
              className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none"
            >
              <option value="orcamento">{t("technicians.baseOrcamentoFull")}</option>
              <option value="faturado">{t("technicians.baseFaturadoFull")}</option>
            </select>
          </Field>

          {/* F-pair-client-pct — remise do cliente no par (só faz sentido em
              base orçamento; vazio = usa a retenção do cadastro do cliente). */}
          {base === "orcamento" && (
            <div className="col-span-2">
              <Field label={t("technicians.fieldClientPct")}>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={cliPct}
                  onChange={(e) => setCliPct(e.target.value)}
                  disabled={busy}
                  placeholder={
                    row.customerRetentionPct != null
                      ? `${Number(row.customerRetentionPct).toFixed(2)}`
                      : ""
                  }
                  className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none"
                />
              </Field>
              <p className="mt-1 text-[11px] text-neutral-500">
                {cliPct === ""
                  ? row.customerRetentionPct != null
                    ? `${t("technicians.clientPctFallbackPre")} ${Number(row.customerRetentionPct).toFixed(2)}% ${t("technicians.clientPctFallbackSuf")}`
                    : t("technicians.clientPctMissing")
                  : t("technicians.clientPctHint")}
              </p>
            </div>
          )}

          {/* F-tcc2 — intermediário do par */}
          <div className="col-span-2 flex items-center gap-3 text-xs">
            <span className="font-medium text-neutral-300">{t("technicians.intermediary")}</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={!hasIntermediary}
                onChange={() => {
                  setHasIntermediary(false);
                  setIntRef("");
                  setIntPct("");
                }}
                disabled={busy}
              />
              <span className="text-neutral-400">{t("technicians.noIntermediary")}</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                checked={hasIntermediary}
                onChange={() => setHasIntermediary(true)}
                disabled={busy}
              />
              <span className="text-neutral-400">{t("technicians.withIntermediary")}</span>
            </label>
          </div>
          {hasIntermediary && (
            <>
              <Field label={t("technicians.intermediary")} required>
                <select
                  value={intRef}
                  onChange={(e) => setIntRef(e.target.value)}
                  disabled={busy}
                  className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none"
                >
                  <option value="">{t("technicians.selectOption")}</option>
                  {intermediaryCandidates.length > 0 && (
                    <optgroup label={t("technicians.optgroupGroupCompanies")}>
                      {intermediaryCandidates.map((c) => (
                        <option key={`c${c.id}`} value={`c:${c.id}`}>
                          {c.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {externalCandidates.length > 0 && (
                    <optgroup label={t("technicians.optgroupExternal")}>
                      {externalCandidates.map((x) => (
                        <option key={`x${x.id}`} value={`x:${x.id}`}>
                          {x.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {intermediaryCandidates.length === 0 && externalCandidates.length === 0 && (
                  <p className="text-xs text-amber-400 mt-1">
                    {t("technicians.noIntermediaryAvailable")}
                  </p>
                )}
              </Field>
              <Field label={t("technicians.fieldIntermediaryPct")} required>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={intPct}
                  onChange={(e) => setIntPct(e.target.value)}
                  disabled={busy}
                  className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none"
                />
              </Field>
            </>
          )}
          {!sumOk && (
            <p className="col-span-2 text-xs text-red-400">
              ⚠ {t("technicians.sumExceedPre")}{" "}
              {(effectiveCliPct + pctNum + effectiveIntPct).toFixed(2)}
              {t("technicians.sumExceedSuf")}
            </p>
          )}

          {/* F-tcc2 — prévia de divisão do par (live, sobre os valores em edição) */}
          <div className="col-span-2">
            <PairPreview
              technicianPct={pctValid ? pctNum : 0}
              intermediaryPct={effectiveIntPct}
              commissionBase={base}
              customerName={row.customerName}
              customerRetentionPct={
                cliPct !== "" && Number.isFinite(cliPctNum)
                  ? String(cliPctNum)
                  : (row.atelierRetentionPct ?? row.customerRetentionPct)
              }
              companyName={billingCompanyName}
            />
          </div>

          <div className="col-span-2 flex items-center justify-between">
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              className="rounded-md border border-red-500/40 hover:border-red-500 text-red-400 disabled:opacity-40 px-3 py-1 text-xs"
            >
              {t("technicians.removeLink")}
            </button>
            <div className="flex items-center gap-3">
              {/* F-tcc-save-hint — explica por que o Salvar está indisponível
                  (senão o botão cinza parece "travado"). */}
              {!valid ? (
                <span className="text-[11px] text-amber-400">
                  {!pctValid
                    ? t("technicians.commissionRange")
                    : !intValid
                      ? t("technicians.fillIntermediary")
                      : t("technicians.sumOver100")}
                </span>
              ) : !dirty ? (
                <span className="text-[11px] text-lime">✓ {t("technicians.savedNoChanges")}</span>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  if (!valid) return;
                  const cid =
                    hasIntermediary && intRef.startsWith("c:")
                      ? Number(intRef.slice(2))
                      : undefined;
                  const xid =
                    hasIntermediary && intRef.startsWith("x:")
                      ? Number(intRef.slice(2))
                      : undefined;
                  onSave(
                    pctNum,
                    base,
                    cid,
                    xid,
                    hasIntermediary ? intPctNum : undefined,
                    cliPct !== "" && Number.isFinite(cliPctNum) ? cliPctNum : null,
                  );
                }}
                disabled={busy || !valid || !dirty}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1 text-xs font-medium"
              >
                {dirty ? t("technicians.save") : t("technicians.savedShort")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * F-tcc2 — Prévia de divisão POR PAR sobre uma base de exemplo (1 000 €),
 * herdeira da antiga prévia do técnico, mas com o cliente do par fixo:
 *   - base ORÇAMENTO: Cliente (retenção do CADASTRO do cliente) + Técnico +
 *     Intermediário + empresa faturadora = 100% sobre o Prix Brut;
 *   - base FATURADO: cliente fora do split (já retido antes da fatura);
 *     Técnico + Intermediário + empresa faturadora = 100% sobre Total Facture.
 * A empresa faturadora ATIVA absorve o resíduo (🔒).
 */
function PairPreview({
  technicianPct,
  intermediaryPct,
  commissionBase,
  customerName,
  customerRetentionPct,
  companyName,
}: {
  technicianPct: number;
  intermediaryPct: number;
  commissionBase: CommissionBase;
  customerName: string;
  customerRetentionPct: string | null;
  companyName: string;
}) {
  const t = useT();
  const BASE = 1000;
  const fmt = (v: number) =>
    new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      v,
    );
  const isOrcamento = commissionBase === "orcamento";
  const customerPct =
    isOrcamento && customerRetentionPct != null && customerRetentionPct !== ""
      ? Number(customerRetentionPct)
      : null;
  const customerMissing = isOrcamento && customerPct == null;
  const residual = 100 - (customerPct ?? 0) - technicianPct - intermediaryPct;
  const residualValid = residual >= 0;

  const lines: { label: string; pct: number | null; locked?: boolean }[] = [];
  if (isOrcamento) lines.push({ label: customerName, pct: customerPct });
  lines.push({ label: t("technicians.technicianLabel"), pct: technicianPct });
  if (intermediaryPct > 0)
    lines.push({ label: t("technicians.intermediary"), pct: intermediaryPct });
  lines.push({ label: `${companyName} 🔒`, pct: residual, locked: true });

  return (
    <div className="rounded border border-neutral-800 bg-neutral-900/40 p-2.5 space-y-1.5">
      <div className="flex items-center justify-between gap-2 text-[11px] text-neutral-500">
        <span className="font-medium text-neutral-400">{t("technicians.previewTitle")}</span>
        <span>
          {t("technicians.previewSampleBase")} ({isOrcamento ? "Prix Brut" : "Total Facture"})
        </span>
      </div>
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-0.5 text-xs">
        {lines.map((l) => (
          <Fragment key={l.label}>
            <span className="text-neutral-400 truncate">{l.label}</span>
            {l.pct == null ? (
              <>
                <span className="text-amber-300 tabular-nums text-right">?</span>
                <span className="text-amber-300 tabular-nums text-right text-[11px]">
                  {t("technicians.noPctRegistered")}
                </span>
              </>
            ) : (
              <>
                <span
                  className={`tabular-nums text-right ${
                    l.locked ? (residualValid ? "text-lime" : "text-red-400") : "text-neutral-300"
                  }`}
                >
                  {l.pct.toFixed(2)}%
                </span>
                <span
                  className={`tabular-nums text-right ${
                    l.locked ? (residualValid ? "text-lime" : "text-red-400") : "text-neutral-300"
                  }`}
                >
                  {fmt((BASE * l.pct) / 100)} €
                </span>
              </>
            )}
          </Fragment>
        ))}
        <span className="border-t border-neutral-700 pt-1 text-neutral-500">
          {t("technicians.totalLabel")}
        </span>
        <span className="border-t border-neutral-700 pt-1 tabular-nums text-right text-neutral-300">
          100.00%
        </span>
        <span
          className={`border-t border-neutral-700 pt-1 tabular-nums text-right ${
            residualValid ? "text-neutral-300" : "text-red-400"
          }`}
        >
          {fmt(BASE)} €{residualValid && !customerMissing ? " ✓" : ""}
        </span>
      </div>
      {customerMissing && (
        <p className="text-[11px] text-amber-300">
          ⚠ {t("technicians.customerMissingPre")} <b>{customerName}</b>{" "}
          {t("technicians.customerMissingSuf")}
        </p>
      )}
      {!residualValid && (
        <p className="text-[11px] text-red-400">
          ⚠ {isOrcamento ? t("technicians.residualExceedOrc") : t("technicians.residualExceedFat")}{" "}
          = {((customerPct ?? 0) + technicianPct + intermediaryPct).toFixed(2)}% &gt; 100%.{" "}
          {companyName} {t("technicians.residualExceedSuf")}
        </p>
      )}
      <p className="text-[10px] text-text-muted">
        {isOrcamento
          ? `${t("technicians.hintOrcPre")} ${companyName} ${t("technicians.hintOrcSuf")}`
          : `${t("technicians.hintFatPre")} ${companyName} ${t("technicians.hintFatSuf")}`}
      </p>
    </div>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const t = useT();
  return active ? (
    <span className="inline-block rounded bg-lime/20 text-lime px-2 py-0.5 text-xs">
      {t("technicians.badgeActive")}
    </span>
  ) : (
    <span className="inline-block rounded bg-neutral-700/40 text-neutral-400 px-2 py-0.5 text-xs">
      {t("technicians.badgeInactive")}
    </span>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}
